import { useNavigate, useParams } from "react-router-dom";
import { useContext, useEffect, useRef, useState } from 'react';
import { GlobalContext } from "components/GlobalContext";
import { Accordion, Button, Card, Col, Form, Row } from "react-bootstrap";
import IEquipment, { IEquipmentCreateOrUpdate, IEquipmentInitResult } from "models/sql/IEquipment";
import EquipmentService from "services/EquipmentService";
import moment from 'moment';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faEnvelope } from "@fortawesome/free-solid-svg-icons";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import IProduct, { IProductFilters } from "models/sql/IProduct";
import ProductService from "services/ProductService";
import IFireStation, { IFireStationFilters } from "models/sql/IFireStation";
import FireStationService from "services/FireStationService";
import IDealer, { IDealerFilters } from "models/sql/IDealer";
import DealerService from "services/DealerService";
import IFireDepartment, { IFireDepartmentFilters } from "models/sql/IFireDepartment";
import FireDepartmentService from "services/FireDepartmentService";
import { addNewLogEntry, generateLog, getBaseDomain, getEnvironment } from "utilities/MiscUtilities";
import ISalesRep, { ISalesRepFilters } from "models/sql/ISalesRep";
import SalesRepService from "services/SalesRepService";
import CountryChooser from "components/CountryChooser";
import MarkdownEditor from "components/MarkdownEditor";
import MiscService from "services/MiscService";
import { useBeforeUnload } from "utilities/UIUtilities";
import DPSEntitySelector from "components/DPSEntitySelector";
import IOpportunity from "models/sql/IOpportunity";
import OpportunityService from "services/OpportunityService";
import { TextField } from "utilities/FormUtility";
import StateChooser from "components/StateChooser";
import DetailFooter from "components/DetailFooter";
import { IFileAttachment } from "models/IFileAttachment";

interface IAirCardDetailProps {
    baseData?: IEquipmentInitResult;
}

const AirCardsDetail = (props: IAirCardDetailProps) => {
    const globalContext = useContext(GlobalContext);
    const globalContextRef = useRef(globalContext);
    globalContextRef.current = globalContext;
    const { id }: any = useParams();
    const [product, setProduct] = useState<IProduct>();
    const [dealer, setDealer] = useState<IDealer>();
    const [salesRep, setSalesRep] = useState<ISalesRep>();
    const [opportunity, setOpportunity] = useState<IOpportunity>();
    const [fireStation, setFireStation] = useState<IFireStation>();
    const [fireDepartment, setFireDepartment] = useState<IFireDepartment>();
    const [equipment, setEquipment] = useState<IEquipment | null>({
        equipmentId: -1,
        serialNumber: "",
        dealerId: undefined,
        fireDepartmentId: undefined,
        extraData: "",
        notes: "",
        parentEquipmentId: undefined,
        productId: undefined,
        registrationDate: undefined,
        startedService: undefined,
        stationId: undefined,
        registrationEmail: "",
        registrationPostalCode: "",
        registrationName: "",
        demoRequestDate: undefined,
        demoApprovalDate: undefined,
        demoStartDate: undefined,
        demoEndDate: undefined,
        demoRequestEmail: "",
        demoRequestName: "",
        systemDataJson: "",
        location: "",
        building: "",
        region: "",
        country: "",
        city: "",
        departmentName: "",
        created: undefined,
        outOfServiceDate: undefined,
        repAssignDate: undefined,
        salesRepId: undefined,
        score: undefined,
        shippingNotes: "",
        stationNumber: "",
        address1: "",
        address2: "",
        state: "",
        postalCode: "",
        contactName: "",
        contactTitle: "",
        contactEmail: "",
        qaCompleteDate: undefined,
        receivedDate: undefined,
        shippedDate: undefined,
        demoDuration: undefined
    });
    const navigate = useNavigate();
    const [hasFormDataChanged, setHasFormDataChanged] = useState(false);
    const [attachments, setAttachments] = useState<IFileAttachment[]>();

    useBeforeUnload(
        hasFormDataChanged,
        "You have unsaved changes, please save them before leaving the page.");

    useEffect(
        () => {
            (async () => {
                if (id !== undefined) {
                    globalContext.setLoading(true);

                    const accessToken = await globalContext.getAccessTokenAsync();
                    const result = await EquipmentService.GetAsync(accessToken, id);

                    if (result.record) {
                        setEquipment(result.record);

                        if (result.record.systemDataJson) {
                            const systemData = JSON.parse(result.record.systemDataJson);
                            if (systemData.files) {
                                setAttachments(systemData.files);
                            }
                        }

                        if (result.record.productId !== null && result.record.productId !== undefined) {
                            const productResult = await ProductService.GetAsync(accessToken, result.record.productId);

                            if (productResult.record) {
                                setProduct(productResult.record);
                            }
                        }

                        if (result.record.stationId !== null && result.record.stationId !== undefined) {
                            const stationResult = await FireStationService.GetAsync(accessToken, result.record.stationId);

                            if (stationResult.record) {
                                setFireStation(stationResult.record);
                            }
                        }

                        if (result.record.fireDepartmentId !== null && result.record.fireDepartmentId !== undefined) {
                            const departmentResult = await FireDepartmentService.GetAsync(accessToken, result.record.fireDepartmentId);

                            if (departmentResult.record) {
                                setFireDepartment(departmentResult.record);
                            }
                        }

                        if (result.record.dealerId !== null && result.record.dealerId !== undefined) {
                            const dealerResult = await DealerService.GetAsync(accessToken, result.record.dealerId);

                            if (dealerResult.record) {
                                setDealer(dealerResult.record);
                            }
                        }

                        if (result.record.salesRepId !== null && result.record.salesRepId !== undefined) {
                            const salesRepResult = await SalesRepService.GetAsync(accessToken, result.record.salesRepId);

                            if (salesRepResult.record) {
                                setSalesRep(salesRepResult.record);
                            }
                        }

                        const opportunityResult =
                            await OpportunityService.GetAllAsync(
                                accessToken,
                                0,
                                99,
                                {
                                    airCardIds: [result.record.equipmentId]
                                });

                        if (opportunityResult.records && opportunityResult.records.length > 0) {
                            setOpportunity(opportunityResult.records[0]);
                        }
                    }
                    else {
                        setEquipment(null);
                        globalContext.showErrorMessage("Unable to load AirCard");
                    }

                    globalContext.setLoading(false);
                }
            })();
        }, [id]);

    const deleteAsync = async () => {
        if (equipment && equipment.equipmentId && window.confirm("Are you SURE you want to delete this?")) {
            globalContext.setLoading(true);
            const accessToken = await globalContext.getAccessTokenAsync();
            await EquipmentService.DeleteAsync(accessToken, equipment.equipmentId);
            globalContext.setLoading(false);
            navigate("/AirCards");
        }
    };

    const submitFormAsync = async (changePage = true, newEquipmentData: IEquipment | null = null) => {
        if (!newEquipmentData) {
            newEquipmentData = equipment;
        }
        else {
            setEquipment(newEquipmentData);
        }

        if (newEquipmentData) {
            globalContext.setLoading(true);
            const accessToken = await globalContext.getAccessTokenAsync();

            const data: IEquipmentCreateOrUpdate = {
                equipmentId: newEquipmentData.equipmentId,
                dealerId: newEquipmentData.dealerId,
                fireDepartmentId: newEquipmentData.fireDepartmentId,
                stationId: newEquipmentData.stationId,
                productId: newEquipmentData.productId,
                serialNumber: newEquipmentData.serialNumber,
                parentEquipmentId: newEquipmentData.parentEquipmentId,
                startedService: newEquipmentData.startedService || undefined,
                registrationDate: newEquipmentData.registrationDate || undefined,
                extraData: newEquipmentData.extraData,
                notes: newEquipmentData.notes,
                registrationEmail: newEquipmentData.registrationEmail,
                registrationName: newEquipmentData.registrationName,
                registrationPostalCode: newEquipmentData.registrationPostalCode,
                demoRequestDate: newEquipmentData.demoRequestDate || undefined,
                demoApprovalDate: newEquipmentData.demoApprovalDate || undefined,
                demoStartDate: newEquipmentData.demoStartDate || undefined,
                demoEndDate: newEquipmentData.demoEndDate || undefined,
                demoRequestName: newEquipmentData.demoRequestName,
                demoRequestEmail: newEquipmentData.demoRequestEmail,
                systemDataJson: newEquipmentData.systemDataJson,
                location: newEquipmentData.location,
                building: newEquipmentData.building,
                region: newEquipmentData.region,
                country: newEquipmentData.country,
                city: newEquipmentData.city,
                departmentName: newEquipmentData.departmentName,
                created: newEquipmentData.created,
                outOfServiceDate: newEquipmentData.outOfServiceDate || undefined,
                repAssignDate: newEquipmentData.repAssignDate || undefined,
                salesRepId: newEquipmentData.salesRepId,
                score: newEquipmentData.score,
                shippingNotes: newEquipmentData.shippingNotes,
                stationNumber: newEquipmentData.stationNumber,
                address1: newEquipmentData.address1,
                address2: newEquipmentData.address2,
                state: newEquipmentData.state,
                postalCode: newEquipmentData.postalCode,
                contactName: newEquipmentData.contactName,
                contactTitle: newEquipmentData.contactTitle,
                contactEmail: newEquipmentData.contactEmail,
                qaCompleteDate: newEquipmentData.qaCompleteDate,
                receivedDate: newEquipmentData.receivedDate,
                shippedDate: newEquipmentData.shippedDate,
                demoDuration: newEquipmentData.demoDuration
            };

            await EquipmentService.CreateOrUpdateAsync(accessToken, data);
            setHasFormDataChanged(false);
            globalContext.setLoading(false);

            if (data.equipmentId === -1) {
                navigate(-1);
            }
        }
    };

    if (equipment) {
        return (
            <>
                <Row>
                    <Col>
                        {
                            (equipment.systemDataJson ?? "").indexOf("follow up") > -1 ?
                                <span className="ps-2 mb-2" style={{ width: "100%", display: "block", textAlign: "center", backgroundColor: "#19875473", padding: "6px", fontWeight: "bold" }}><FontAwesomeIcon icon={faCheck as IconProp} /> Followed Up</span> :
                                <Button
                                    style={{ width: "100%" }}
                                    variant="success"
                                    title="Send Follow Up Email"
                                    className="quick-link mb-2"
                                    onClick={async (e) => {
                                        if (!equipment.registrationEmail) {
                                            window.alert("This equipment must first have a registered email address before you can automatically follow up.");
                                            return;
                                        }

                                        e.stopPropagation();
                                        e.preventDefault();
                                        globalContext.showModal({
                                            size: "xl",
                                            content:
                                                <></>,
                                            title: "Send Follow Up Email",
                                            formElements: [
                                                {
                                                    key: "title",
                                                    type: "text",
                                                    title: `Email Title`,
                                                    value: "Regarding your AirCard Result"
                                                },
                                                {
                                                    key: "body",
                                                    type: "textarea",
                                                    title: `Body`,
                                                    value: `We recently received your AirCard Result and would like to discuss.`
                                                }
                                            ],
                                            okCallback:
                                                async (data) => {
                                                    var emailAddress = equipment.registrationEmail;
                                                    var fromEmailAddress = globalContext.userEmail;
                                                    var title = data?.[0].value ?? "";
                                                    var body = data?.[1].value ?? "";

                                                    if (emailAddress) {
                                                        const accessToken = await globalContext.getAccessTokenAsync();

                                                        await MiscService.sendEmailRawAsync(
                                                            accessToken,
                                                            emailAddress,
                                                            fromEmailAddress,
                                                            title,
                                                            body);

                                                        await submitFormAsync(
                                                            false,
                                                            addNewLogEntry(
                                                                (new Date()),
                                                                `${globalContext.userName} has sent an aircard follow up email to '${equipment.registrationEmail}'`,
                                                                globalContext.userEmail,
                                                                101,
                                                                equipment));

                                                        window.alert("Your follow up email has been sent to " + emailAddress + ".");
                                                    }
                                                },
                                            cancelCallback: () => { },
                                            customActionButton: {
                                                title: "Manually Contacted",
                                                buttonType: "success",
                                                onClick: async () => {
                                                    await submitFormAsync(
                                                        false,
                                                        addNewLogEntry(
                                                            (new Date()),
                                                            `${globalContext.userName} manually sent a follow up email to '${equipment.registrationEmail}'`,
                                                            globalContext.userEmail,
                                                            101,
                                                            equipment));

                                                    window.alert("Your manual follow up has been noted.");
                                                },
                                                closesModal: true
                                            }
                                        });
                                    }}>
                                    <FontAwesomeIcon icon={faEnvelope as IconProp} /> Send Follow Up Email
                                </Button>
                        }

                        {
                            !!equipment.score &&
                            <>{
                                (equipment.systemDataJson ?? "").indexOf("sent the aircard test") > -1 ?
                                    <span className="ps-2" style={{ width: "100%", display: "block", textAlign: "center", backgroundColor: "#19875473", padding: "6px", fontWeight: "bold" }}><FontAwesomeIcon icon={faCheck as IconProp} /> AirCard Results Sent</span> :
                                    <Button
                                        style={{ width: "100%" }}
                                        variant="warning"
                                        title="Send Test Results"
                                        className="quick-link"
                                        onClick={async (e) => {
                                            if (!equipment.registrationEmail) {
                                                window.alert("This equipment must first have a registered email address before you can send results.");
                                                return;
                                            }

                                            e.stopPropagation();
                                            e.preventDefault();

                                            var reportUrl = `https://${getBaseDomain()}/Public/AircardReport?serial=${equipment.serialNumber}&postal=${equipment.registrationPostalCode}`;

                                            var emailBody =
                                                (getEnvironment() != "prod" ? "<h1 style=\"color: red\">TEST EMAIL</h1>" : "") +
                                                "Hello from TFT Health.<br/><br/>Thank you for using the AirCard to assess your air quality. The score is calculated based on the location tested, number of days in place, and the percentage of purple color lost in the test chamber.<br/><br/>" +
                                                `You can <a target="_blank" href="${reportUrl}">view your results here</a>.<br/><br/>` +
                                                "TFT Health Team";

                                            globalContext.showModal({
                                                size: "xl",
                                                content:
                                                    <></>,
                                                title: "Send Test Results",
                                                formElements: [
                                                    {
                                                        key: "title",
                                                        type: "text",
                                                        title: `Email Title`,
                                                        value: "AirCard Results"
                                                    },
                                                    {
                                                        key: "body",
                                                        type: "textarea",
                                                        title: `Body`,
                                                        value: emailBody
                                                    }
                                                ],
                                                okCallback:
                                                    async (data) => {
                                                        var emailAddress = equipment.registrationEmail;

                                                        if (equipment.salesRep && equipment.salesRep.email) {
                                                            emailAddress += `,${equipment.salesRep.email}`;
                                                        }

                                                        var fromEmailAddress = "NoReply@TFTHealth.com";
                                                        var title = (getEnvironment() !== "prod" ? "TEST EMAIL - " : "") + data?.[0].value ?? "";
                                                        var body = data?.[1].value ?? "";

                                                        if (emailAddress) {
                                                            const accessToken = await globalContext.getAccessTokenAsync();

                                                            await MiscService.sendEmailRawAsync(
                                                                accessToken,
                                                                emailAddress,
                                                                fromEmailAddress,
                                                                title,
                                                                body);

                                                            await submitFormAsync(
                                                                false,
                                                                addNewLogEntry(
                                                                    (new Date()),
                                                                    `${globalContext.userName} has sent the aircard test results email to '${equipment.registrationEmail}'`,
                                                                    globalContext.userEmail,
                                                                    101,
                                                                    equipment));

                                                            window.alert("Test results email have been sent to " + emailAddress + ".");
                                                        }
                                                    },
                                                cancelCallback: () => { }
                                            });
                                        }}>
                                        <FontAwesomeIcon icon={faEnvelope as IconProp} /> Send AirCard Test Results
                                    </Button>
                            }
                            </>
                        }
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <Card className="mb-2 mt-2">
                            <Card.Body>
                                <Card.Title>
                                    {
                                        id !== undefined &&
                                        <strong>{equipment.product?.name} ({equipment.product?.partNumber})</strong>
                                    }
                                    {
                                        equipment.outOfServiceDate && equipment.score !== undefined && equipment.score !== null &&
                                        <>
                                            <br />
                                            <a href={`/public/AircardReport?serial=${equipment.serialNumber}&postal=${equipment.registrationPostalCode}`} target="_blank">View Report</a>
                                        </>
                                    }
                                    {
                                        opportunity &&
                                        <>
                                            <br />
                                            <a href={`/dps/Opportunities/Detail/${opportunity.opportunityId}`} target="_blank">View Lead / Opportunity</a>
                                        </>
                                    }
                                    {
                                        id === undefined &&
                                        <strong>New AirCard</strong>
                                    }
                                </Card.Title>

                                <DPSEntitySelector<IProduct, IProductFilters>
                                    entityFilterSearchPropName={"names"}
                                    entityService={ProductService}
                                    entityTypeName="Product"
                                    defaultEntityFilter={{ serialPrefixs: ["d01", "r01"] }}
                                    selectedEntityRef={product}
                                    generateEntityTitle={(entry) => entry.name}
                                    onClickExternalHandler={(entry) => {
                                        if (entry) {
                                            window.open(`/DPS/Products/Detail/${entry.productId}`);
                                        }
                                    }}
                                    entityChangeHandler={(newEntry) => {
                                        setEquipment({ ...equipment, productId: newEntry?.productId || undefined });
                                        setProduct(newEntry);
                                        setHasFormDataChanged(true);
                                    }}
                                />

                                <DPSEntitySelector<IFireDepartment, IFireDepartmentFilters>
                                    entityFilterSearchPropName={"fireDeptNames"}
                                    entityService={FireDepartmentService}
                                    entityTypeName="Department"
                                    defaultEntityFilter={{}}
                                    selectedEntityRef={fireDepartment}
                                    generateEntityTitle={(entry) => `${entry.fireDeptName} ${entry.hqcity} ${entry.hqstate}, ${entry.hqzip}`}
                                    onClickExternalHandler={(entry) => {
                                        if (entry) {
                                            window.open(`/DPS/Departments/Detail/${entry.fireDepartmentId}`);
                                        }
                                    }}
                                    onClickAddNewHandler={() => {
                                        window.open(`/DPS/Departments/Detail/?defs={"fireDeptName":"${equipment.departmentName ?? ""}","hqstate":"","hqCountry":"${equipment.country ?? ""}"}`);
                                    }}
                                    entityChangeHandler={(newEntry) => {
                                        setEquipment({ ...equipment, fireDepartmentId: newEntry?.fireDepartmentId || undefined });
                                        setFireDepartment(newEntry);
                                        setHasFormDataChanged(true);
                                    }}
                                />

                                <DPSEntitySelector<IFireStation, IFireStationFilters>
                                    entityFilterSearchPropName={"postalCodes"}
                                    entityService={FireStationService}
                                    entityTypeName="Station"
                                    defaultEntityFilter={{}}
                                    selectedEntityRef={fireStation}
                                    generateEntityTitle={(entry) => `${entry.name} (${entry.number})`}
                                    onClickExternalHandler={(entry) => {
                                        if (entry) {
                                            window.open(`/DPS/Stations/Detail/${entry.fireStationId}`);
                                        }
                                    }}
                                    entityChangeHandler={(newEntry) => {
                                        setEquipment({ ...equipment, stationId: newEntry?.fireStationId || undefined });
                                        setFireStation(newEntry);
                                        setHasFormDataChanged(true);
                                    }}
                                />

                                <DPSEntitySelector<IDealer, IDealerFilters>
                                    entityFilterSearchPropName={"dealerNames"}
                                    entityService={DealerService}
                                    entityTypeName="Dealer"
                                    defaultEntityFilter={{}}
                                    selectedEntityRef={dealer}
                                    generateEntityTitle={(entry) => entry.dealerName ?? ""}
                                    onClickExternalHandler={(entry) => {
                                        if (entry) {
                                            window.open(`/DPS/Dealers/Detail/${entry.dealerId}`);
                                        }
                                    }}
                                    entityChangeHandler={(newEntry) => {
                                        setEquipment({ ...equipment, dealerId: newEntry?.dealerId || undefined });
                                        setDealer(newEntry);
                                        setHasFormDataChanged(true);
                                    }}
                                />

                                <DPSEntitySelector<ISalesRep, ISalesRepFilters>
                                    entityFilterSearchPropName={"names"}
                                    entityService={SalesRepService}
                                    entityTypeName="Rep"
                                    defaultEntityFilter={{ dealerIds: dealer ? [dealer.dealerId] : undefined }}
                                    selectedEntityRef={salesRep}
                                    generateEntityTitle={(entry) => entry?.fullName ?? ""}
                                    onClickExternalHandler={(entry) => {
                                        if (entry) {
                                            window.open(`/DPS/Reps/Detail/${entry.salesRepId}`);
                                        }
                                    }}
                                    entityChangeHandler={(newEntry) => {
                                        setEquipment({ ...equipment, salesRepId: newEntry?.salesRepId || undefined });
                                        setSalesRep(newEntry);
                                        setHasFormDataChanged(true);
                                    }}
                                />

                                <Row className="align-items-end">
                                    <TextField
                                        title="Serial Number"
                                        value={equipment.serialNumber}
                                        onChange={newVal => {
                                            setEquipment({ ...equipment, serialNumber: newVal });
                                            setHasFormDataChanged(true);
                                        }}
                                        changeTracker={setHasFormDataChanged}
                                        readOnly={!globalContext.roles.some(r => r === "Web.Admin")}
                                    />
                                    <TextField
                                        title="Station #"
                                        value={equipment.building}
                                        onChange={newVal => {
                                            setEquipment({ ...equipment, building: newVal });
                                            setHasFormDataChanged(true);
                                        }}
                                        changeTracker={setHasFormDataChanged}
                                        readOnly={!globalContext.roles.some(r => r === "Web.Admin")}
                                    />
                                    <TextField
                                        title="Placement Location"
                                        value={equipment.location}
                                        onChange={newVal => {
                                            setEquipment({ ...equipment, location: newVal });
                                            setHasFormDataChanged(true);
                                        }}
                                        changeTracker={setHasFormDataChanged}
                                        readOnly={!globalContext.roles.some(r => r === "Web.Admin")}
                                    />
                                </Row>

                                <Row>
                                    <TextField
                                        title="Address 1"
                                        value={equipment.address1}
                                        onChange={newVal => {
                                            setEquipment({ ...equipment, address1: newVal });
                                            setHasFormDataChanged(true);
                                        }}
                                        changeTracker={setHasFormDataChanged}
                                        readOnly={!globalContext.roles.some(r => r === "Web.Admin")}
                                    />
                                    <TextField
                                        title="Address 2"
                                        value={equipment.address2}
                                        onChange={newVal => {
                                            setEquipment({ ...equipment, address2: newVal });
                                            setHasFormDataChanged(true);
                                        }}
                                        changeTracker={setHasFormDataChanged}
                                        readOnly={!globalContext.roles.some(r => r === "Web.Admin")}
                                    />
                                    <TextField
                                        title="City"
                                        extraContent={
                                            <>
                                                {
                                                    equipment.fireDepartment &&
                                                    <>
                                                        &nbsp;(Dept: {equipment.fireDepartment.hqcity})<br />
                                                    </>
                                                }
                                            </>
                                        }
                                        value={equipment.city}
                                        onChange={newVal => {
                                            setEquipment({ ...equipment, city: newVal });
                                            setHasFormDataChanged(true);
                                        }}
                                        changeTracker={setHasFormDataChanged}
                                        readOnly={!globalContext.roles.some(r => r === "Web.Admin")}
                                    />
                                </Row>

                                <Row>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>State</Form.Label>
                                            {
                                                equipment.fireDepartment &&
                                                <>
                                                    &nbsp;(Dept: {equipment.fireDepartment.hqstate})<br />
                                                </>
                                            }
                                            {
                                                <StateChooser
                                                    onChange={newVal => {
                                                        setEquipment({ ...equipment, state: newVal });
                                                        setHasFormDataChanged(true);
                                                    }}
                                                    defaultValue={equipment.state}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Postal Code</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setEquipment({ ...equipment, postalCode: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={equipment.postalCode}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Country</Form.Label>
                                            {
                                                equipment.fireDepartment &&
                                                <>
                                                    &nbsp;(Dept: {equipment.fireDepartment.hqCountry})<br />
                                                </>
                                            }
                                            <CountryChooser
                                                onChange={newVal => {
                                                    setEquipment({ ...equipment, country: newVal });
                                                    setHasFormDataChanged(true);
                                                }}
                                                defaultValue={equipment.country}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Registration Name</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    readOnly={!globalContext.roles.some(r => r === "Web.Admin")}
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setEquipment({ ...equipment, registrationName: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={equipment.registrationName}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Registration Postal Code</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    readOnly={!globalContext.roles.some(r => r === "Web.Admin")}
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setEquipment({ ...equipment, registrationPostalCode: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={equipment.registrationPostalCode}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col className={fireDepartment && (!fireDepartment.emailSuffix || (fireDepartment.emailSuffix && equipment.registrationEmail?.toLocaleLowerCase().indexOf(fireDepartment.emailSuffix) == -1)) ? "col-danger" : ""}>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Registration Email</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    readOnly={!globalContext.roles.some(r => r === "Web.Admin")}
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setEquipment({ ...equipment, registrationEmail: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={equipment.registrationEmail}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Temp Department Name</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    readOnly={!globalContext.roles.some(r => r === "Web.Admin")}
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setEquipment({ ...equipment, departmentName: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={equipment.departmentName}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group
                                            className="mb-3"
                                            style={{
                                                backgroundColor: ((equipment.score && (equipment.systemDataJson ?? "").indexOf("sent the aircard test") === -1) ? "rgba(255,0,0,0.2" : "inherit"),
                                            }} >
                                            <Form.Label>Score</Form.Label>
                                            <Form.Control
                                                type="text"
                                                readOnly={!globalContext.roles.some(r => r === "Web.Admin")}
                                                onChange={
                                                    (e: React.ChangeEvent<HTMLInputElement>) => {
                                                        setEquipment({ ...equipment, score: parseInt(e.target.value || "0") });
                                                        setHasFormDataChanged(true);
                                                    }
                                                }
                                                value={equipment.score || 0}
                                            />
                                            <span style={{ fontSize: "0.8rem", fontStyle: "italic" }}>
                                                {
                                                    (equipment.score && (equipment.systemDataJson ?? "").indexOf("sent the aircard test") === -1) &&
                                                    <>
                                                        <strong>Don't forget to send the results along to the end user!</strong><br /><br />
                                                    </>
                                                }
                                                1-7 = "Very High"<br />
                                                8 - 14 = "High to Very High"<br />
                                                15 - 21 = "Moderate to High"<br />
                                                22+ = "Low to Moderate"<br />
                                                -1 = "Low"
                                            </span>
                                        </Form.Group>
                                    </Col>
                                    <Col></Col>
                                </Row>

                                <Row className="pb-3">
                                    <Col>
                                        <Accordion style={{ border: "1px solid #0d6efd", borderRadius: "0.375rem" }}>
                                            <Accordion.Item
                                                eventKey="0"
                                                style={{
                                                    "backgroundColor": "#eee"
                                                }}>
                                                <Accordion.Header><strong>Dates / Timeline</strong></Accordion.Header>
                                                <Accordion.Body>
                                                    <Row>
                                                        <Col>
                                                            <Row className="align-items-end">

                                                                <Col>
                                                                    <Form.Group className="mb-3" >
                                                                        <Form.Label>Initial (Created) Date</Form.Label>
                                                                        {
                                                                            <Form.Control
                                                                                type="date"
                                                                                onChange={
                                                                                    (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                                        setEquipment({ ...equipment, created: e.target.value ? e.target.value : undefined });
                                                                                        setHasFormDataChanged(true);
                                                                                    }
                                                                                }
                                                                                value={equipment.created ? moment(equipment.created).format('yyyy-MM-DD') : undefined}
                                                                            />
                                                                        }
                                                                    </Form.Group>
                                                                </Col>
                                                                <Col>
                                                                    <Form.Group className="mb-3" >
                                                                        <Form.Label>Ship Date</Form.Label>
                                                                        {
                                                                            <Form.Control
                                                                                type="date"
                                                                                readOnly={!globalContext.roles.some(r => r === "Web.Admin")}
                                                                                onChange={
                                                                                    (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                                        setEquipment({ ...equipment, shippedDate: e.target.value ? e.target.value : undefined });
                                                                                        setHasFormDataChanged(true);
                                                                                    }
                                                                                }
                                                                                value={equipment.shippedDate ? moment(equipment.shippedDate).format('yyyy-MM-DD') : undefined}
                                                                            />
                                                                        }
                                                                    </Form.Group>
                                                                </Col>
                                                                <Col>
                                                                    <Form.Group className="mb-3" >
                                                                        <Form.Label>Started Service</Form.Label>
                                                                        {
                                                                            <Form.Control
                                                                                type="date"
                                                                                onChange={
                                                                                    (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                                        setEquipment({ ...equipment, startedService: e.target.value ? e.target.value : undefined });
                                                                                        setHasFormDataChanged(true);
                                                                                    }
                                                                                }
                                                                                value={equipment.startedService ? moment(equipment.startedService).format('yyyy-MM-DD') : undefined}
                                                                            />
                                                                        }
                                                                    </Form.Group>
                                                                </Col>
                                                                <Col>
                                                                    <Form.Group className="mb-3" >
                                                                        <Form.Label>Registration Date</Form.Label>
                                                                        {
                                                                            <Form.Control
                                                                                type="date"
                                                                                onChange={
                                                                                    (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                                        setEquipment({ ...equipment, registrationDate: e.target.value ? e.target.value : undefined });
                                                                                        setHasFormDataChanged(true);
                                                                                    }
                                                                                }
                                                                                value={equipment.registrationDate ? moment(equipment.registrationDate).format('yyyy-MM-DD') : undefined}
                                                                            />
                                                                        }
                                                                    </Form.Group>
                                                                </Col>
                                                                <Col>
                                                                    <Form.Group className="mb-3" >
                                                                        <Form.Label>Out of Service Date</Form.Label>
                                                                        {
                                                                            <Form.Control
                                                                                type="date"
                                                                                onChange={
                                                                                    (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                                        setEquipment({ ...equipment, outOfServiceDate: e.target.value ? e.target.value : undefined });
                                                                                        setHasFormDataChanged(true);
                                                                                    }
                                                                                }
                                                                                value={equipment.outOfServiceDate ? moment(equipment.outOfServiceDate).format('yyyy-MM-DD') : undefined}
                                                                            />
                                                                        }
                                                                    </Form.Group>
                                                                </Col>
                                                            </Row>
                                                        </Col>
                                                    </Row>
                                                </Accordion.Body>
                                            </Accordion.Item>
                                        </Accordion>
                                    </Col>
                                </Row>

                                {
                                    globalContext.roles.some(r => r === "Web.Admin") &&
                                    <Row>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Admin Notes</Form.Label>
                                            <MarkdownEditor
                                                globalContextRef={globalContextRef.current}
                                                noun="Note"
                                                onChange={newVal => {
                                                    setEquipment({ ...equipment, notes: newVal });
                                                    setHasFormDataChanged(true);
                                                }}
                                                value={equipment.notes || ""}
                                            />
                                        </Form.Group>
                                    </Row>
                                }
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                <Row className="pb-3">
                    <Col>
                        <Accordion style={{ border: "1px solid #0d6efd", borderRadius: "0.375rem" }}>
                            <Accordion.Item
                                eventKey="0"
                                style={{
                                    "backgroundColor": "#eee"
                                }}>
                                <Accordion.Header><strong>Attachments</strong></Accordion.Header>
                                <Accordion.Body>
                                    {
                                        attachments && attachments.length > 0 ?
                                            attachments.map((attachment, index) => {
                                                return (
                                                    <div key={index}>
                                                        <h3>{attachment.title}</h3>
                                                        <img style={{ width: "100%", maxHeight: "2000px" }} src={`/api/Misc/downloadFile?fileName=${attachment.fileName}`} />
                                                        <br /><br />
                                                    </div>
                                                );
                                            }) :
                                            <span>No attachments</span>
                                    }
                                </Accordion.Body>
                            </Accordion.Item>
                        </Accordion>
                    </Col>
                </Row>

                {
                    globalContext.roles.some(r => r === "Web.Admin") &&
                    <Row className="align-items-end">
                        <Col>
                            <Card className="mb-2">
                                <Card.Body>
                                    <Card.Title>
                                        <strong>Log</strong><br />
                                    </Card.Title>
                                    {generateLog<IEquipment>(equipment)}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                }

                {
                    globalContext.roles.some(r => r === "Web.Admin") &&
                    <DetailFooter
                        hasFormDataChanged={hasFormDataChanged}
                        deleteAsync={deleteAsync}
                        submitFormAsync={submitFormAsync}
                    />
                }
            </>
        );
    }
    else {
        return (
            <>
                <h1>There was an issue retreiving the AirCard, please try again.</h1>
            </>
        );
    }
}

export default AirCardsDetail;
